import type { Urls } from 'Common/Urls';

/*
	Builds the asset URL for an image field's resolved value. `value` is what the field
	path resolved to on the record — either a string URL (returned as-is) or a stored
	image value ({hash, formats, ...}). Returns null when there is nothing renderable.

	The field path selects the asset folder: `theme.*` variables live in PosterTheme-image,
	`lineup.*` images in Lineup-image, everything else in Event-image. The shared flag comes
	from the resolved value itself: a shared theme's images carry `shared` (see
	themeVariables) and live under __SHARED, while a record's own images (event, lineup)
	never do — they live under the venue's site key, even when the poster is being designed
	in the admin's shared builder.

	This is the single source of truth for field-image URL resolution — ImageObject
	(field source and theme-bound upload source) and modeSwitch (user-mode baking) all
	use it, so their folder/shared logic can't drift apart.
*/
export function fieldImageUrl(
	field: string | undefined,
	value: unknown,
	urls: Pick<Urls, 'imageUrl'>,
	proxyProductionAssets: boolean,
): string | null
{
	if (value == null) return null;
	if (typeof value === 'string') return value;
	if (typeof value !== 'object') return null;

	const v = value as Record<string, unknown>;
	if (!v.hash || !v.formats) return null;

	//FIXME the non-theme folder choice here is event specific (cf Special-image for specials)
	const isTheme = field?.startsWith('theme.') ?? false;
	const folder  = isTheme                      ? 'PosterTheme-image'
	              : field?.startsWith('lineup.') ? 'Lineup-image'
	              :                                 'Event-image';

	const fmt = (v.formats as Record<string, { format?: string } | undefined>).medium?.format;
	if (!fmt) return null;

	return urls.imageUrl(folder, Boolean(v.shared), v.hash as string, 'medium', fmt, (v.dev as boolean) ?? false, proxyProductionAssets);
}
